import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { SketchPicker } from 'react-color';
import { getUserData } from '../../services';
import PageCustomizationTab from './PageCustomizationTab';
import ButtonCustomizationTab from './ButtonCustomizationTab';
import QrCodeCustomizationTab from './QrCodeCustomizationTab';

const Customize = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get tab from URL query parameter, fall back to localStorage if no query param exists, then default to 'page'
  const getTabFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    // If URL has a tab parameter, use it
    if (tab && (tab === 'page' || tab === 'button' || tab === 'qrcode')) {
      return tab;
    }
    
    // Otherwise fall back to localStorage if it exists
    const storedTab = localStorage.getItem('tipl_activeTab');
    if (storedTab && (storedTab === 'page' || storedTab === 'button' || storedTab === 'qrcode')) {
      // Update URL to match localStorage (on the next render cycle to avoid issues)
      setTimeout(() => {
        navigate(`/customize?tab=${storedTab}`, { replace: true });
      }, 0);
      return storedTab;
    }
    
    // Default to 'page' if neither URL nor localStorage has a valid tab
    return 'page';
  };

  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [linkId, setLinkId] = useState(null);
  const [buttonId, setButtonId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [isKeyboardUser, setIsKeyboardUser] = useState(false);

  const pagePanelRef = useRef(null);
  const buttonPanelRef = useRef(null);
  const qrCodePanelRef = useRef(null);

  // Update URL when tab changes
  const updateTabInUrl = (tab) => {
    // Update URL
    navigate(`/customize?tab=${tab}`, { replace: true });
    
    // Also store in localStorage for backward compatibility
    localStorage.setItem('tipl_activeTab', tab);
    
    setActiveTab(tab);
  };

  useEffect(() => {
    const handleFirstTab = (e) => {
      if (e.key === 'Tab') {
        setIsKeyboardUser(true);
        window.removeEventListener('keydown', handleFirstTab);
      }
    };
    window.addEventListener('keydown', handleFirstTab);
    return () => {
      window.removeEventListener('keydown', handleFirstTab);
    };
  }, []);

  useEffect(() => {
    // Only update active tab from URL when it's different from current
    const urlTab = getTabFromUrl();
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [location.search]); // Only depend on search portion of URL to avoid loops

  useEffect(() => {
    const fetchEssentialData = async () => {
      if (!(linkId === null || buttonId === null || qrCode === null)) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const userData = await getUserData(['linkId', 'qrCode']);

        if (userData.linkId) setLinkId(userData.linkId);
        if (userData.qrCode) setQrCode(userData.qrCode);

        const buttonIdResponse = await fetch(`${apiBaseUrl}/link-to-button/${userData.linkId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!buttonIdResponse.ok) {
          throw new Error(`Failed to fetch buttonId: ${buttonIdResponse.status}`);
        }

        const buttonIdData = await buttonIdResponse.json();
        if (buttonIdData.buttonId) setButtonId(buttonIdData.buttonId);
      } catch (error) {
        console.error('Error fetching buttonId:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEssentialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isKeyboardUser) return;

    if (activeTab === 'page') {
      pagePanelRef.current?.focus();
    } else if (activeTab === 'button') {
      buttonPanelRef.current?.focus();
    } else if (activeTab === 'qrcode') {
      qrCodePanelRef.current?.focus();
    }
  }, [activeTab, isKeyboardUser]);

  return (
    <div className="flex md:min-h-[calc(100vh-3rem)]">
      {/* Sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <div className="page-background w-full max-w-4xl mx-auto p-4 relative min-[768px]:ml-48 min-[1250px]:ml-auto self-start">
        {isLoading ? (
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#68C7CC]"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6 max-w-6xl mx-auto mt-12 mb-12 w-full h-full">
            <h1 className="sr-only">Customize</h1>
            <div role="tablist" aria-label="Customization options" className="flex justify-center md:justify-start border-b mb-6">
              <button
                role="tab"
                aria-selected={activeTab === 'page'}
                aria-controls="page-panel"
                id="page-tab"
                className={`pb-4 px-6 font-medium ${activeTab === 'page'
                  ? 'text-[#68C7CC] border-b-2 border-[#68C7CC]'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => {
                  setIsKeyboardUser(false);
                  updateTabInUrl('page');
                }}
              >
                Page
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'button'}
                aria-controls="button-panel"
                id="button-tab"
                className={`pb-4 px-6 font-medium ${activeTab === 'button'
                  ? 'text-[#68C7CC] border-b-2 border-[#68C7CC]'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => {
                  setIsKeyboardUser(false);
                  updateTabInUrl('button');
                }}
              >
                Button
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'qrcode'}
                aria-controls="qrcode-panel"
                id="qrcode-tab"
                className={`pb-4 px-6 font-medium ${activeTab === 'qrcode'
                  ? 'text-[#68C7CC] border-b-2 border-[#68C7CC]'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
                onClick={() => {
                  setIsKeyboardUser(false);
                  updateTabInUrl('qrcode');
                }}
              >
                QR code
              </button>
            </div>

            <div ref={pagePanelRef} id="page-panel" role="tabpanel" tabIndex="0" aria-labelledby="page-tab">
              {activeTab === 'page' && <PageCustomizationTab linkId={linkId} />}
            </div>
            <div ref={buttonPanelRef} id="button-panel" role="tabpanel" tabIndex="0" aria-labelledby="button-tab">
              {activeTab === 'button' && <ButtonCustomizationTab buttonId={buttonId} SketchPicker={SketchPicker} />}
            </div>
            <div ref={qrCodePanelRef} id="qrcode-panel" role="tabpanel" tabIndex="0" aria-labelledby="qrcode-tab">
              {activeTab === 'qrcode' && <QrCodeCustomizationTab linkId={linkId} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customize;
